import type { PromptType } from '../types/prompt'
import type { SocialMedia } from '@/types/user'

export const LINKS = {
  roadmap: 'https://snack-prompt.canny.io/',
  featureRequests: 'https://snack-prompt.canny.io/feature-requests',
  support: 'https://snack-prompt.canny.io/support',
  supportEmail: 'hello@snackprompt.com',
  magicPage: '/magic',

  tools: {
    'ChatGPT': 'https://chat.openai.com/chat',
    'Gemini': 'https://gemini.google.com/',
    'Midjourney': 'https://alpha.midjourney.com/imagine',
    'Dall-E': 'https://chat.openai.com/chat',
    'Google LM': '',
  },

  extension: {
    chromeWebStore:
      'https://chrome.google.com/webstore/detail/snack-prompt/lgpdceoebipofiijjgpiedpfcdenkndi?hl=en-US',
    magicChromeWebStore: 'https://b.link/Magic-Website',
  },
}

export const TRENDING_TOPICS = [8, 23, 4, 2, 1]
export const TRENDING_TOPICS_VISUAL = [48, 59, 14, 35, 13]

export const SEO = {
  title: 'Snack Prompt | Discover The Best ChatGPT Prompts',
  url: 'https://snackprompt.com',
  description:
    'Explore a community-driven platform to discover, upvote, and share the best AI prompts for ChatGPT & Gemini. Follow topics, create and organize prompts, and connect with expert prompters. Unlock AI’s full potential with Snack Prompt.',
  image: 'https://snackprompt.com/snackprompt-banner.jpeg',
  twitter: '@SnackPrompt',
  keywords: [
    'Snack Prompt',
    'AI prompts',
    'ChatGPT',
    'artificial intelligence',
    'upvote prompts',
    'create prompts',
    'AI collaboration',
    'prompters',
    'AI prompt community',
    'AI topics',
    'AI prompt platform',
  ],
}

export const SOCIAL_MEDIAS = [
  'website',
  'twitter',
  'x',
  'discord',
  'github',
  'facebook',
  'instagram',
  'tiktok',
  'pinterest',
  'linkedin',
  'youtube',
] as const

export const SOCIAL_MEDIA_URLS: Record<SocialMedia, string> = {
  website: '',
  twitter: 'twitter.com',
  x: 'x.com',
  discord: 'discord.gg',
  github: 'github.com',
  facebook: 'facebook.com',
  instagram: 'instagram.com',
  tiktok: 'tiktok.com',
  pinterest: 'pinterest.com',
  linkedin: 'linkedin.com/in',
  youtube: 'youtube.com',
}

export const PROMPT_CREATE = {
  maxOutputImages: 4,
  maxCoverImages: 4,
  minVisualPromptImages: 1,
}

export const MAX_FILE_SIZE = 5 // MB

export const ALLOWED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
] as string[]

export enum PROMPT_TYPES {
  Text = 1,
  Visual,
  Video,
  Audio,
  ChatGPT,
}

export const PROMPT_TYPE_OBJECTS: PromptType[] = [
  {
    id: 1,
    name: 'Text',
    active: true,
    tools: ['ChatGPT', 'Gemini'],
    elementalType: 'prompt',
  },
  {
    id: 2,
    name: 'Visual',
    active: true,
    tools: [
      'Dall-E',
      'Midjourney',
      'ChatGPT',
      'Gemini',
    ],
    elementalType: 'prompt',
  },
  {
    id: 3,
    name: 'Video',
    active: false,
    tools: ['Google LM'],
    elementalType: 'prompt',
  },
  {
    id: 4,
    name: 'Audio',
    active: false,
    tools: undefined,
    elementalType: 'prompt',
  },
  {
    id: 5,
    name: 'ChatGPT',
    active: false,
    tools: undefined,
    elementalType: 'output',
  },
  {
    id: 6,
    name: 'Gemini',
    active: false,
    tools: undefined,
    elementalType: 'output',
  },
  {
    id: 7,
    name: 'Claude',
    active: false,
    tools: undefined,
    elementalType: 'output',
  },
  {
    id: 8,
    name: 'Other',
    active: false,
    tools: undefined,
    elementalType: 'output',
  },
  {
    id: 9,
    name: 'Make',
    active: true,
    tools: undefined,
    elementalType: 'automation',
  },
  {
    id: 10,
    name: 'Zapier',
    active: true,
    tools: undefined,
    elementalType: 'automation',
  },
  {
    id: 11,
    name: 'Tines',
    active: false,
    tools: undefined,
    elementalType: 'automation',
  },
  {
    id: 12,
    name: 'IFTTT',
    active: false,
    tools: undefined,
    elementalType: 'automation',
  },
  {
    id: 13,
    name: 'Outfunnel',
    active: false,
    tools: undefined,
    elementalType: 'automation',
  },
  {
    id: 14,
    name: 'Other',
    active: true,
    tools: undefined,
    elementalType: 'automation',
  },
]

export const MAX_TUTORIAL_STEPS = 7

export const YOUTUBE_LINK_REGEX
  = /(?:youtube(?:-nocookie)?\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/

export const GALLERY_SIZES: Record<number, number[]> = {
  1: [624],
  2: [312, 312],
  3: [416, 208, 208],
  4: [468, 156, 156, 156],
}

export const STRIPE_CURRENCY_CONVERSIONS: Record<string, number> = {
  // Decided not to use an API since it doesn't need to be 100% precise and to keep the code simpler
  // Needs to update values from time to time to update currency prices
  // Leave prices a little higher to prevent losing money from price fluctuations
  // Has to be updated in the backend and frontend
  // Last updated 9/5/2023

  aed: 4,
  aud: 2,
  bgn: 2,
  cad: 2,
  chf: 1,
  czk: 30,
  dkk: 10,
  eur: 1,
  gbp: 2,
  gip: 1,
  hkd: 10,
  hrk: 10,
  huf: 500,
  jpy: 200,
  ltl: 5,
  lvl: 1,
  mxn: 25,
  nok: 15,
  nzd: 2,
  pln: 6,
  ron: 6,
  sek: 20,
  sgd: 2,
  thb: 40,
  usd: 1,
}

export const STRIPE_CURRENCY_SYMBOL: Record<string, string> = {
  aed: 'د.إ',
  aud: 'A$',
  bgn: 'Лв',
  cad: 'C$',
  chf: 'Fr',
  czk: 'Kč',
  dkk: 'Kr',
  eur: '€',
  gbp: '£',
  gip: '£',
  hkd: 'HK$',
  hrk: 'kn',
  huf: 'Ft',
  jpy: '¥',
  ltl: '5',
  lvl: 's',
  mxn: 'Mex$',
  nok: 'kr',
  nzd: 'NZ$',
  pln: 'zł',
  ron: 'leu',
  sek: 'kr',
  sgd: 'S$',
  thb: '฿',
  usd: '$',
}
